import { isEmpty } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import type React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import styled from "styled-components";

import {
  EmailVerificationStatus,
  EMAIL_VERIFICATION_CODE_LENGTH,
  AccessMode,
  DeploymentTypeEnum,
} from "@superblocksteam/shared";

import logo from "assets/images/logo.svg";
import VerificationInput from "core/components/app/EmailVerification/VerificationInput";
import { Spinner } from "core/components/Spinner/Spinner";
import { SUPERBLOCKS_UI_DEPLOYMENT_TYPE } from "env";
import { LOGOUT_URL, TERMS_URL, PRIVACY_URL } from "legacy/constants/routes";
import { getCurrentUser, getAccessMode } from "legacy/selectors/usersSelectors";
import { PageWrapper } from "pages/components/PageWrapper";
import { REQUEST_CODE, VERIFY_EMAIL } from "pages/routes";
import { callServer, HttpMethod } from "store/utils/client";
import logger from "utils/logger";

import { IntercomOverlay } from "../Intercom/IntercomOverlay";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fbfaf9;
  backdrop-filter: blur(10px);
  z-index: 100;
`;

const Card = styled.div`
  width: 440px;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow:
    0px 0px 0px 1px rgba(14, 20, 27, 0.16),
    0px 6px 12px 0px rgba(14, 20, 27, 0.06);
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  padding: 48px;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
`;

const LogoWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 9px;
  background: linear-gradient(135deg, #575757 0%, #000000 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const LogoImg = styled.img`
  width: 32px;
  height: 32px;
`;

const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;
  width: 100%;
`;

const Heading = styled.h1`
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 20px;
  line-height: 28px;
  color: #131416;
  margin: 0;
`;

const Subtitle = styled.p`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #767a7f;
  margin: 0;
`;

const EmailHighlight = styled.span`
  color: #131416;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const VerifyButton = styled.button`
  width: 100%;
  padding: 12px 20px;
  border: none;
  border-radius: 10px;
  background-color: #131416;
  color: white;
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 17px;
  line-height: 24px;
  cursor: pointer;
  position: relative;
  box-shadow:
    0px 0px 0px 1px black,
    0px 1px 2px 0px rgba(27, 30, 34, 0.08),
    0px 3px 8px 1px rgba(14, 20, 27, 0.16);

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    box-shadow: inset 0px 1px 0px 0px rgba(255, 255, 255, 0.3);
    pointer-events: none;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ResendText = styled.p`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #767a7f;
  text-align: center;
  margin: 0;
`;

const ResendLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #0099ff;
  text-decoration: underline;
  text-decoration-color: rgba(0, 153, 255, 0.12);
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 24px;
  background-color: #fbfaf9;
  border-top: 1px solid rgba(71, 58, 31, 0.16);
`;

const FooterText = styled.p`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #aaadb1;
  text-align: center;
  margin: 0;
  max-width: 296px;
`;

const FooterLink = styled.a`
  color: #767a7f;
  text-decoration: underline;
  text-decoration-color: #caccce;
`;

const NoticeWrapper = styled.div<{ $type: "success" | "error" }>`
  width: 100%;
  padding: 12px 20px;
  border-radius: 10px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 20px;
  border: 1px solid
    ${({ $type }) => ($type === "error" ? "#FF3341" : "#52c41a")};
  background-color: ${({ $type }) =>
    $type === "error" ? "rgba(255, 51, 65, 0.03)" : "rgba(82, 196, 26, 0.03)"};
  color: ${({ $type }) => ($type === "error" ? "#FF3341" : "#52c41a")};
`;

const LogoutLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #767a7f;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: #caccce;

  &:hover {
    color: #4d5156;
  }
`;

const EmailVerificationWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const currentUser = useSelector(getCurrentUser);
  const emailAddress = useMemo(
    () => currentUser?.email ?? "",
    [currentUser?.email],
  );
  const accessMode = useSelector(getAccessMode);

  const codeLength = EMAIL_VERIFICATION_CODE_LENGTH;
  const emptyValues = Array(codeLength).fill("");
  const [values, setValues] = useState(emptyValues);

  const [resendLoading, setResendLoading] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);

  const [notice, setNotice] = useState<
    { message: string; type: "success" | "error" } | undefined
  >();
  const [hasSentEmail, setHasSentEmail] = useState(false);

  const requestNewCode = useCallback(
    (email: string | undefined, force = false) => {
      if (isEmpty(email)) {
        logger.error(`Email verification code requested without and email`);
      }
      if (force) {
        setNotice(undefined);
        setResendLoading(true);
      }
      return callServer<boolean>({
        method: HttpMethod.Post,
        url: REQUEST_CODE,
        body: {
          email: email,
          force: force,
        },
      }).finally(() => {
        if (force) {
          setResendLoading(false);
          setNotice({
            message: `A new code has been sent to ${emailAddress}`,
            type: "success",
          });
          setHasSentEmail(true);
          setTimeout(() => setHasSentEmail(false), 5000);
        }
      });
    },
    [emailAddress],
  );

  const verifyCode = useCallback(async (email: string, code: string) => {
    setVerificationLoading(true);
    try {
      const res = await callServer<{
        success: boolean;
        error: string;
      }>({
        method: HttpMethod.Post,
        url: VERIFY_EMAIL,
        body: {
          email: email,
          code: code,
        },
      });
      if (res.success) {
        setNotice({
          message: "Email successfully verified! Redirecting...",
          type: "success",
        });
        window.location.reload();
      } else {
        setNotice({
          message: res.error,
          type: "error",
        });
      }
    } finally {
      setVerificationLoading(false);
    }
  }, []);

  const resetValues = useCallback(() => {
    setValues(emptyValues);
  }, [emptyValues]);

  const onComplete = useCallback(
    (vals: string[]) => {
      return verifyCode(emailAddress, vals.join(""));
    },
    [emailAddress, verifyCode],
  );

  const handleVerifyClick = useCallback(() => {
    const code = values.join("");
    if (code.length === codeLength && !values.some(isEmpty)) {
      verifyCode(emailAddress, code);
    }
  }, [values, codeLength, emailAddress, verifyCode]);

  useEffect(() => {
    if (
      SUPERBLOCKS_UI_DEPLOYMENT_TYPE !== DeploymentTypeEnum.CLOUD_PREM &&
      accessMode === AccessMode.AUTH_USER &&
      currentUser?.emailVerificationStatus !== EmailVerificationStatus.VERIFIED
    ) {
      requestNewCode(emailAddress);
    }
  }, [
    emailAddress,
    currentUser?.emailVerificationStatus,
    requestNewCode,
    accessMode,
  ]);

  const navigate = useNavigate();
  const handleLogout = useCallback(
    () => navigate({ pathname: LOGOUT_URL }),
    [navigate],
  );

  if (
    SUPERBLOCKS_UI_DEPLOYMENT_TYPE === DeploymentTypeEnum.CLOUD_PREM ||
    accessMode === AccessMode.VISITOR ||
    accessMode === AccessMode.EXTERNAL_USER ||
    currentUser?.emailVerificationStatus === EmailVerificationStatus.VERIFIED
  ) {
    return <>{children}</>;
  }

  const isCodeComplete =
    values.join("").length === codeLength && !values.some(isEmpty);
  return (
    <IntercomOverlay>
      <PageWrapper pageName={"Email Verification"}>
        <Overlay>
          <Card>
            <CardBody>
              <HeaderSection>
                <LogoWrapper>
                  <LogoImg src={logo} alt="Superblocks" />
                </LogoWrapper>
                <TextGroup>
                  <Heading>Verify your email</Heading>
                  <Subtitle>
                    We sent a verification link to:
                    <br />
                    <EmailHighlight>{emailAddress}</EmailHighlight>
                  </Subtitle>
                </TextGroup>
              </HeaderSection>

              <FormSection>
                <VerificationInput
                  length={codeLength}
                  values={values}
                  setParentValues={setValues}
                  onComplete={onComplete}
                  resetValues={resetValues}
                />
                {notice && (
                  <NoticeWrapper $type={notice.type}>
                    {notice.message}
                  </NoticeWrapper>
                )}
                <VerifyButton
                  onClick={handleVerifyClick}
                  disabled={!isCodeComplete || verificationLoading}
                >
                  {verificationLoading ? (
                    <Spinner size={24} inline />
                  ) : (
                    "Verify your email"
                  )}
                </VerifyButton>
                <ResendText>
                  {`Didn't get the code? `}
                  <ResendLink
                    onClick={() => requestNewCode(emailAddress, true)}
                    disabled={resendLoading || hasSentEmail}
                  >
                    {hasSentEmail ? "Sent!" : "Resend"}
                  </ResendLink>
                </ResendText>
              </FormSection>
            </CardBody>

            <CardFooter>
              <FooterText>
                {`By continuing, you agree to Superblocks' `}
                <FooterLink href={TERMS_URL} target="_blank" rel="noreferrer">
                  Terms of Service
                </FooterLink>
                {` and `}
                <FooterLink href={PRIVACY_URL} target="_blank" rel="noreferrer">
                  Privacy Policy
                </FooterLink>
              </FooterText>
            </CardFooter>
          </Card>

          <LogoutLink
            onClick={handleLogout}
            style={{
              position: "absolute",
              bottom: 24,
              right: 24,
            }}
          >
            Logout
          </LogoutLink>
        </Overlay>
      </PageWrapper>
    </IntercomOverlay>
  );
};

EmailVerificationWrapper.displayName = "Verify email";

export default EmailVerificationWrapper;
